/**
 * Agent Signatures Redux Middleware
 *
 * Async thunks for signature API operations
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import api, { AxiosError } from 'api';
import type { SignatureCreateRequest, SignatureUpdateRequest } from 'types/signature';

/**
 * Load all signatures for the current user
 */
export const loadSignatures = createAsyncThunk(
    'api/signatures(GET)',
    async (_, { rejectWithValue }) => {
        try {
            return await api.signatures.list();
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

/**
 * Load a single signature by ID
 */
export const loadSignature = createAsyncThunk(
    'api/signatures/:id(GET)',
    async (signatureId: number, { rejectWithValue }) => {
        try {
            return await api.signatures.get(signatureId);
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

/**
 * Create a new signature
 * Automatically refreshes the signatures list after creation
 */
export const createSignature = createAsyncThunk(
    'api/signatures(POST)',
    async (payload: SignatureCreateRequest, { dispatch, rejectWithValue }) => {
        try {
            const response = await api.signatures.create(payload);
            // Refresh signatures list after creation
            dispatch(loadSignatures());
            return response;
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

/**
 * Update signature metadata
 * Only metadata fields can be updated (display_name, title, signature_name)
 * Automatically refreshes the signatures list after update
 */
export const updateSignature = createAsyncThunk(
    'api/signatures/:id(PUT)',
    async (
        { id, updates }: { id: number; updates: SignatureUpdateRequest },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const response = await api.signatures.update(id, updates);
            // Refresh signatures list after update
            dispatch(loadSignatures());
            return response;
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

/**
 * Delete a signature (soft delete - marks as inactive)
 * Automatically refreshes the signatures list after deletion
 */
export const deleteSignature = createAsyncThunk(
    'api/signatures/:id(DELETE)',
    async (signatureId: number, { dispatch, rejectWithValue }) => {
        try {
            await api.signatures.remove(signatureId);
            // Refresh signatures list after deletion
            dispatch(loadSignatures());
            return signatureId;
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);

/**
 * Set a signature as the default
 * This will unmark any previous default signature
 * Automatically refreshes the signatures list after update
 */
export const setDefaultSignature = createAsyncThunk(
    'api/signatures/:id/default(PUT)',
    async (signatureId: number, { dispatch, rejectWithValue }) => {
        try {
            const response = await api.signatures.setDefault(signatureId);
            // Refresh signatures list to update all default flags
            dispatch(loadSignatures());
            return response;
        } catch (e) {
            return rejectWithValue((e as AxiosError).response?.data);
        }
    }
);
